import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import Browser from "components/Modal/ChooseFilesModal/Browser";
import Details from "components/Modal/ChooseFilesModal/Details";
import Navigation from "components/Modal/ChooseFilesModal/Navigation";
import SplitVerticalPanels from "components/SplitVerticalPanels";
import { UPLOAD_ENTITY_TYPES } from "constants/documentUploads";
import { modalTypes } from "constants/types";
import { arrayOf, func, instanceOf, string } from "prop-types";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { selectSelectedCollection } from "slices/collections/selectors";
import { resetState, setFiles } from "slices/workspace/chooseFilesModalSlice";
import {
  selectChooseFilesModal,
  selectFiles,
} from "slices/workspace/selectors";

import { fetchSharepointEnabled } from "../../../slices/sharepoint/actions";
import { resetSharepointState } from "../../../slices/sharepoint/sharepointSlice";
import { createUploadContinueHelper } from "./helpers";
import { allowedExtensionsProp } from "./propTypes";

const ChooseFilesModal = ({
  allowedExtensions,
  target,
  droppedFiles,
  onFilesUploaded = () => {},
}) => {
  const dispatch = useDispatch();
  const { processingFiles } = useSelector(selectChooseFilesModal);
  const selectedFiles = useSelector(selectFiles);
  const selectedCollection = useSelector(selectSelectedCollection);
  const { activeSource } = useSelector(selectChooseFilesModal);

  const { handleContinue, processFiles } = createUploadContinueHelper({
    selectedCollection,
    dispatch,
    files: selectedFiles,
    activeSource,
  });

  useEffect(() => {
    dispatch(fetchSharepointEnabled());
    dispatch(resetSharepointState());
    dispatch(setFiles([]));
  }, []);

  const closeModal = () => {
    dispatch(resetState());
    dispatch(hideModal(modalTypes.CHOOSE_FILES));
  };

  const onContinue = async () => {
    dispatch(hideModal(modalTypes.CHOOSE_FILES));
    await dispatch(handleContinue(target, onFilesUploaded));
  };

  const handleCancel = () => {
    // If not on collections page
    dispatch(resetSharepointState());
    closeModal();
  };

  // If any dropped files provided, process them and show "Upload files modal"
  useEffect(() => {
    if (!droppedFiles?.length) {
      return;
    }

    const processedDroppedFiles = async () => {
      await processFiles(droppedFiles);
      onContinue();
    };

    processedDroppedFiles();
  }, []);

  // If user dropped files this modal should be skipped and "Upload files modal" will be shown
  if (droppedFiles?.length) {
    return null;
  }

  return (
    <Modal
      className="choose_files_modal"
      onDismiss={closeModal}
      restrictWidth={false}
      backdrop
      portal
      show
    >
      <Modal.Header className="choose_files_modal__header">
        <span>Choose Files</span>
      </Modal.Header>
      <Modal.Body className="choose_files_modal__body">
        <Navigation showSharepoint={target !== UPLOAD_ENTITY_TYPES.FOLDER} />
        <SplitVerticalPanels
          panels={[
            {
              children: (
                <Browser
                  allowedExtensions={allowedExtensions}
                  target={target}
                  processFiles={processFiles}
                />
              ),
              className: "choose_files_modal__body__browser",
              initialSize: "531px",
              minSize: "220px",
            },
            {
              children: <Details target={target} />,
              className: "choose_files_modal__body__details",
              initialSize: "354px",
              minSize: "220px",
            },
          ]}
        />
      </Modal.Body>
      <Modal.Footer className="choose_files_modal__footer">
        <NewButton variant={NewButtonVariant.l2} onClick={handleCancel}>
          Cancel
        </NewButton>

        <NewButton
          variant={NewButtonVariant.l1}
          disabled={!selectedFiles.length || processingFiles}
          onClick={onContinue}
        >
          Continue
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

ChooseFilesModal.propTypes = {
  allowedExtensions: allowedExtensionsProp,
  // Upload target (folder, opportunity ...)
  target: string.isRequired,
  // List of files user dropped on summary page
  droppedFiles: arrayOf(instanceOf(File)),
  onFilesUploaded: func.isRequired,
};

export default ChooseFilesModal;
